import 'primeicons/primeicons.css';
import './lara-light-indigo-sorgenia/theme.css';
import 'primereact/resources/primereact.css';
import 'primeflex/primeflex.css';
import { Routes, Route, BrowserRouter as Router } from "react-router-dom";
import './App.scss';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal, MsalAuthenticationTemplate } from "@azure/msal-react";
import { AccountInfo, InteractionStatus, InteractionType } from "@azure/msal-browser";
import React, { useEffect, useRef, useState } from "react";
import ProfiliCaricoContext, { MainContext } from "./Context/ProfiliCaricoContext";
import { Toast } from "primereact/toast";
import HeaderToolbar from "./Components/HeaderToolbar/HeaderToolbar";
import { dataRequest, loginRequest } from "./AuthConfig";
import { ErrorComponent } from "./Components/ui-components/ErrorComponent";
import { Loading } from "./Components/ui-components/Loading";
import AppRoutes from "./Utils/AppRoutes";
import NotFoundPage from "./Pages/NotFoundPage/NotFoundPage";
import MapPage from "./Pages/Map/MapPage";
import { HomePlant } from "./Services/typescript-fetch-client";
import setLocale from "./Utils/setLocale";
import CCGTPage from './Pages/CCGT/CCGTPage';
import RenewPage from './Pages/Renew/RenewPage';
import AppRoleChecker from "./Components/ui-components/AppRoleChecker";
import isMobile from "is-mobile";

function App() {
    const [inProgressApi, setInProgressApi] = useState<boolean>(false);
    const [accessToken, setAccessToken] = useState<(string | null)>(null);
    const { instance, inProgress, accounts } = useMsal();
    const [username, setUserName] = useState<string | undefined>(instance.getActiveAccount()?.name);
    const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
    const isAuthenticated = useIsAuthenticated();
    const toastError = useRef<Toast>(null);

    const initContext: MainContext = {
        inProgressApi: inProgressApi,
        setInProgressApi: setInProgressApi,
        isAuthenticated: isAuthenticated,
        accessToken: accessToken,
        username: username,
        setAccessToken: setAccessToken,
        sidebarIsOpen: isSidebarOpen,
        setSidebarIsOpen: setIsSidebarOpen,
        logoutAction: () => {instance.logoutRedirect()},
        doAcquireTokenRedirect: () => {
            instance.acquireTokenRedirect({
                ...dataRequest,
                account: instance.getActiveAccount() as AccountInfo
            });
        },
        showMsg: (errorMsg: string, errorDtl?: string, severity?: "success" | "info" | "warn" | "error", sticky?: boolean) => {
            toastError.current?.show({
                severity: severity ?? 'error',
                summary: errorMsg,
                detail: errorDtl,
                sticky: sticky ?? true
            });
        }
    }

    setLocale();

    useEffect(() => {
        let login = () =>{
            const accessTokenRequest = {
                ...dataRequest,
                account: accounts[0]
            };
    
            if (inProgress === InteractionStatus.None) {
                instance.acquireTokenSilent(accessTokenRequest).then((response) => {
                    setAccessToken(prev => {
                        return prev === response.accessToken ? prev : response.accessToken;
                    });
                    setUserName(response.idTokenClaims.hasOwnProperty("given_name") ? response.idTokenClaims["given_name"] : response.account?.name);
                }).catch((e) => {
                    instance.acquireTokenPopup(accessTokenRequest).then((response) => {
                        setAccessToken(prev => {
                            return prev === response.accessToken ? prev : response.accessToken;
                        });
                        setUserName(response.idTokenClaims.hasOwnProperty("given_name") ? response.idTokenClaims["given_name"] : response.account?.name);
                    });
                });
            }
        };
        
        login();

        //Periodically renew access token. Alternative is to call acquireTokenSilent on every apiCall (https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/2799)
        let interval = setInterval(login, 1000 * 60 * 5);
        return () => {
            clearInterval(interval);
        }
    }, [inProgress, instance, accounts]);

    const authRequest = {
        ...dataRequest
    };

    return (
        <ProfiliCaricoContext.Provider value={initContext}>
            <Router>
                <Toast ref={toastError} />
                <div className={isMobile({featureDetect: true, tablet: true}) ? 'mobile' : ''}>
                        <MsalAuthenticationTemplate
                            interactionType={InteractionType.Redirect}
                            authenticationRequest={authRequest}
                            errorComponent={ErrorComponent}
                            loadingComponent={Loading}
                        >
                            <AuthenticatedTemplate>
                                <AppRoleChecker enabled={true} roleName={"Auth.Access"}>
                                    <>
                                        <HeaderToolbar toggleSidebar={() => { setIsSidebarOpen(!isSidebarOpen); }} inProgressApi={inProgressApi} />
                                        <div id="page-container">
                                            <Routes>
                                                <Route path={AppRoutes.HOME_ROUTE} element={<MapPage />} key="map" />
                                                <Route path={AppRoutes.CCGT_ROUTE} element={<CCGTPage plantType={HomePlant.TypeEnum.Ccgt} />} key="ccgt" />
                                                <Route path={AppRoutes.CCGT_ROUTE_MODE_SINGLE} element={<CCGTPage plantType={HomePlant.TypeEnum.Ccgt} key="ccgtSingle" />} />
                                                <Route path={AppRoutes.WIND_ROUTE} element={<RenewPage plantType={HomePlant.TypeEnum.Wind} key="wind" />} />
                                                <Route path={AppRoutes.WIND_ROUTE_MODE_SINGLE} element={<RenewPage plantType={HomePlant.TypeEnum.Wind} key="windSingle" />} />
                                                <Route path={AppRoutes.BIO_ROUTE} element={<RenewPage plantType={HomePlant.TypeEnum.Bioenergy} key="bioenergy" />} />
                                                <Route path={AppRoutes.BIO_ROUTE_MODE_SINGLE} element={<RenewPage plantType={HomePlant.TypeEnum.Bioenergy} key="bioenergySingle" />} />
                                                <Route path="*" element={<NotFoundPage />} />
                                            </Routes>
                                        </div>
                                    </>
                                </AppRoleChecker>
                            </AuthenticatedTemplate>
                            <UnauthenticatedTemplate>
                                <h1>Completa l'autenticazione per poter accedere all'applicazione</h1>
                            </UnauthenticatedTemplate>
                        </MsalAuthenticationTemplate>
                </div>
            </Router>
        </ProfiliCaricoContext.Provider>
    );
}

export default App;
